import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import SearchIcon from "@mui/icons-material/Search";
import { setAlert } from "../../../../action/alert";
import Alert from "../../../layout/Alert";
import AppButton from "../../../Common/Button/Button";
import FormInput from "../../../Common/FormInputs/MuiTextField";
import DataTable from "../../../Common/DataTable/DataTable";
import EmptyState from "../../../Common/EmptyState/EmptyState";
import { SectionHeading } from "../../../Common/Typography/AppTypography";
import { format } from "date-fns";
import { dacStatsPageSize } from "../DACStatisticsConstants";

const DeviceDACLogs = ({
    deviceDetails,
    dacData = [],
    getDeviceDACLogsData,
    showSearch = false,
    showCount = false,
    setCustomAlert,
}) => {
    const [deviceDACLogsData, setDeviceDACLogsData] = useState([]);
    const [updateFlag, setUpdateFlag] = useState(undefined);
    const [isLoading, setIsLoading] = useState(!showSearch);
    const [pageIndex, setPageIndex] = useState(0);
    const [macAddress, setMacAddress] = useState("");
    const [hasSearched, setHasSearched] = useState(false);

    const headers = {
        serialNumber: "S.No",
        timestamp: 'Created At'
    };

    useEffect(() => {
        if (showSearch) return;
        if (deviceDetails && deviceDetails instanceof Object && Object.prototype.hasOwnProperty.call(deviceDetails, "macAddress")) {
            getDeviceDACLogs()
        } else {
            setDeviceDACLogsData([]);
            setIsLoading(false);
        }
    }, [deviceDetails, showSearch])

    useEffect(() => {
        if (updateFlag) {
            getDeviceDACLogs()
        }
    }, [updateFlag])

    useEffect(() => {
        setDeviceDACLogsData(Array.isArray(dacData) ? dacData : [])
    }, [dacData])

    const filter = () => {
        setUpdateFlag(Math.random());
    }

    const getNextPage = () => {
        const newPg = pageIndex + 1
        setPageIndex(newPg);
        setIsLoading(true)
        getDeviceDACLogsData({
            macAddress,
            pgIndex: newPg,
            pgSize: dacStatsPageSize
        }, () => {
            setIsLoading(false)
        })
    }

    const getPrevPage = () => {
        const newPg = pageIndex - 1;
        setPageIndex(newPg);
        setIsLoading(true)
        getDeviceDACLogsData({
            macAddress,
            pgIndex: newPg,
            pgSize: dacStatsPageSize
        }, () => {
            setIsLoading(false)
        })
    }

    const getDeviceDACLogs = () => {
        setIsLoading(true)
        getDeviceDACLogsData({ macAddress: deviceDetails?.macAddress ?? "" }, () => {
            setIsLoading(false);
        })
    }

    const searchDACLogs = () => {
        if (!macAddress || macAddress.trim() === "") {
            setCustomAlert("MAC Address Cannot be Empty", "danger");
            return;
        }
        setIsLoading(true)
        setHasSearched(true);
        setPageIndex(0);
        getDeviceDACLogsData({
            macAddress,
            pgIndex: 0,
            pgSize: dacStatsPageSize
        }, () => {
            setIsLoading(false)
        })
    }

    const deviceDACLogColumns = Object.keys(headers).map((key) => ({
        key,
        label: headers[key],
        minWidth: key === "timestamp" ? 220 : 100,
        cellSx: { wordBreak: "break-word", whiteSpace: "normal" }
    }));

    const getTimestampMs = (record) => {
        if (record == null) return null;

        if (typeof record === "number" || typeof record === "string") {
            const n = Number(record);
            if (!Number.isFinite(n)) return null;
            return n > 1e12 ? n : n * 1000;
        }

        if (typeof record === "object") {
            const raw = record.ts ?? record.timestamp ?? record.createdAt ?? record.time;
            const n = Number(raw);
            if (!Number.isFinite(n)) return null;
            return n > 1e12 ? n : n * 1000;
        }

        return null;
    };

    const formatDacTimestamp = (record) => {
        const tsMs = getTimestampMs(record);
        if (!tsMs) return "-";
        const dt = new Date(tsMs);
        if (Number.isNaN(dt.getTime())) return "-";
        return format(dt, "dd MMM yyyy, hh:mm a");
    };

    const logsArray = Array.isArray(deviceDACLogsData) ? deviceDACLogsData : [];
    const deviceDACLogRows = logsArray.map((element, index) => ({
        rowId: `dac-${index}`,
        serialNumber: index + 1,
        timestamp: formatDacTimestamp(element)
    }));
    const hasDACLogData = deviceDACLogRows.length > 0;
    const showLogResults = showSearch ? hasSearched : true;

    return (
        <Box sx={{ width: "100%", minWidth: 0, mt: 2, mb: 2 }}>
            {showSearch && (
                <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1, mb: 2 }}>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, flexWrap: "wrap", alignItems: { xs: "stretch", sm: "center" }, gap: { xs: 1.5, sm: 2 } }}>
                        <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 200px" }, minWidth: 0, maxWidth: { xs: "100%", sm: 320 }, width: { xs: "100%", sm: "auto" } }}>
                            <FormInput
                                type="text"
                                size="small"
                                placeholder="Enter MAC address"
                                value={macAddress}
                                onChange={(e) => setMacAddress(e.target.value)}
                                startAdornment={<SearchIcon />}
                                sx={{ width: "100%" }}
                            />
                        </Box>
                        <AppButton
                            variant="primary"
                            size="medium"
                            onClick={() => searchDACLogs()}
                            sx={{ minWidth: 80, height: 40, width: { xs: "100%", sm: "auto" } }}
                        >
                            Filter
                        </AppButton>
                    </Box>
                </Paper>
            )}
            <Alert />

            {isLoading ? (
                <Box sx={{ display: "grid", placeItems: "center", minHeight: "40vh", py: 2 }}>
                    <CircularProgress sx={{ color: "#AFB42B" }} size={60} />
                </Box>
            ) : (
                <Box sx={{ width: "100%", minWidth: 0 }}>
                    {showCount && hasSearched && (
                        <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1, mb: 2 }}>
                            <SectionHeading sx={{ m: 0 }}>
                                DAC Count : {Array.isArray(dacData) ? dacData.length : "-"}
                            </SectionHeading>
                        </Paper>
                    )}
                    {showLogResults && (
                        <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1, overflow: "hidden" }}>
                            {!hasDACLogData ? (
                                <EmptyState
                                    message="No Data found"
                                    sx={{ bgcolor: "transparent", p: 0 }}
                                />
                            ) : (
                                <DataTable
                                    columns={deviceDACLogColumns}
                                    data={deviceDACLogRows}
                                    rowKey="rowId"
                                    minWidth="100%"
                                    maxHeight="58vh"
                                    striped={false}
                                    hoverEffect={false}
                                    msgForDisplay="No Data found"
                                    sx={{
                                        width: "100%",
                                        minWidth: 0,
                                        overflowX: "auto",
                                        scrollBehavior: "auto",
                                    }}
                                    tableContainerSx={{
                                        boxShadow: "none",
                                        borderRadius: 0,
                                        width: "100%",
                                        minWidth: 0,
                                    }}
                                />
                            )}
                        </Paper>
                    )}
                </Box>
            )}
        </Box>
    );
};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch) => ({
    setCustomAlert: (message, alertType) => dispatch(setAlert(message, alertType))
});

export default connect(mapStateToProps, mapDispatchToProps)(DeviceDACLogs);
